import { Box, Button, Container, Typography, styled } from '@mui/material'
import Grid from '@mui/material/Unstable_Grid2'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { IRootState } from '../../../../../redux/types'
import Header from '../../../../shared/components/Header'
import useScreenWidth from '../../../../shared/hooks/useScreenWidth'
import SubHeader from '../../../../shared/styles/Subheader'
import { isSuperAdmin } from '../../../users/utils/RolesHelper'
import BestCTR from '../components/BestCTR'
import MostPopularContent from '../components/MostPopularContent'
import { StatCard } from '../styles/GraphsStyles'
import DateRangeFilter from '../components/DateRangeFilter'
import { QuickRangeKey, RANGE_DEFS } from '../hooks/dateRanges'


export const ButtonOption = styled(Button)(({ theme }) => ({
  height: 110,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    maxWidth: 292
  },
  '&:hover': {
    border: 'transparent',
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.common.white
  }
}))

function mockStatsByRange(startISO: string, endISO: string) {
  const start = dayjs(startISO)
  const end = dayjs(endISO)
  const days = Math.max(1, end.startOf('day').diff(start.startOf('day'), 'day') + 1)
  const factor = days / 30 // base mensual

  const base = { visits: 12000, users: 850, events: 300, news: 42 }
  const seed = Number(start.format('YYYYMMDD')) % 97
  const jitter = (n: number) => Math.round(n * (1 + (seed % 7) / 100)) // hasta +7%

  return {
    visits: jitter(Math.round(base.visits * factor)),
    users: jitter(Math.round(base.users * factor)),
    events: jitter(Math.round(base.events * factor)),
    news: jitter(Math.round(Math.max(5, base.news * factor)))
  }
}

function Dashboard() {
  const { t } = useTranslation(['global'])
  const isMobile = useScreenWidth(700)
  const navigate = useNavigate()
  const { user } = useSelector((state: IRootState) => state.auth)

  const [quickRange, setQuickRange] = useState<QuickRangeKey>('today')
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')

  const [stats, setStats] = useState({ visits: 0, users: 0, events: 0, news: 0 })
  useEffect(() => {
    if (quickRange !== 'custom') {
      const { start, end } = RANGE_DEFS[quickRange](dayjs())
      setStartDate(start.format('YYYY-MM-DD'))
      setEndDate(end.format('YYYY-MM-DD'))
    }
  }, [quickRange])

  useEffect(() => {
    if (startDate && endDate) {
      setStats(mockStatsByRange(startDate, endDate))
    }
  }, [startDate, endDate])

  let menuItems: any = []
  if (user !== null) {
    if (user.role_id === 3) {
      menuItems = [{ id: 1, name: t('admin.people.people'), path: '/admin/usuarios' }]
    } else if ([8, 9].includes(user.role_id)) {
      menuItems = [{ id: 1, name: t('admin.people.people'), path: '/admin/usuarios' }]
    } else if ([1, 2, 5].includes(user.role_id)) {
      menuItems = [
        { id: 1, name: t('slider'), path: '/admin/sliders' },
        { id: 2, name: t('offers'), path: '/admin/ofertas' },
        { id: 3, name: t('updates'), path: '/admin/novedades' },
        { id: 4, name: t('news'), path: '/admin/noticias' },
        { id: 5, name: t('admin.library.library'), path: '/admin/biblioteca' },
        { id: 6, name: t('academy'), path: '/admin/cursos' },
        { id: 7, name: t('events'), path: '/admin/events' },
        { id: 8, name: t('seminars'), path: '/admin/seminarios' }
      ]
    }
  }

  return (
    <>
      <Header title={t('common.admin')} />
      <SubHeader />
      {isSuperAdmin(user.role_id) ? (
        <Container maxWidth="xl" style={{ marginTop: isMobile ? '16px' : '36px' }}>
          <DateRangeFilter
            t={t as any}
            quickRange={quickRange}
            onQuickRangeChange={setQuickRange}
            startDate={startDate}
            endDate={endDate}
            onStartChange={setStartDate}
            onEndChange={setEndDate}
          />

          <Grid container spacing={2} mb={4}>
            <Grid xs={12} sm={6} md={3}>
              <StatCard>
                <Typography variant="h6">{t('views')}</Typography>
                <Typography variant="h4">{stats.visits.toLocaleString()}</Typography>
              </StatCard>
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <StatCard>
                <Typography variant="h6">{t('users')}</Typography>
                <Typography variant="h4">{stats.users.toLocaleString()}</Typography>
              </StatCard>
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <StatCard>
                <Typography variant="h6">{t('courses')}</Typography>
                <Typography variant="h4">{stats.events.toLocaleString()}</Typography>
              </StatCard>
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <StatCard>
                <Typography variant="h6">{t('resources')}</Typography>
                <Typography variant="h4">{stats.news.toLocaleString()}</Typography>
              </StatCard>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid xs={12} lg={6}>
              <MostPopularContent />
            </Grid>
            <Grid xs={12} lg={6} style={{ borderBottom: 'solid 1px #C6C6C6' }}>
              <BestCTR />
            </Grid>
            {/* <Grid xs={12} lg={6}><RankingUsers /></Grid>
            <Grid xs={12} lg={6}><PopularContent /></Grid> */}
          </Grid>
        </Container>
      ) : (
        <Container>
          <Box
            sx={theme => ({
              paddingTop: theme.spacing(2),
              [theme.breakpoints.up('sm')]: { padding: theme.spacing(4) }
            })}
          >
            <Typography variant="h6" align="center">
              {t('client.register.welcome-to-plei', { APP_NAME: import.meta.env.VITE_NAME })}{' '}
              {user ? user.name : ''}, {t('manage-today')}
            </Typography>
          </Box>
          <Box pt={4} display="flex" justifyContent="center" gap={isMobile ? '24px' : '48px'} flexWrap="wrap">
            {menuItems.map((item: any) => (
              <ButtonOption key={item.name} variant="outlined" color="secondary" onClick={() => navigate(`${item.path}`)}>
                <Typography variant={isMobile ? 'subtitle1' : 'body1'}>{item.name}</Typography>
              </ButtonOption>
            ))}
          </Box>
        </Container>
      )}
    </>
  )
}

export default Dashboard
